import {
  CloseFullscreenRounded,
  HubRounded,
  OpenInFullRounded,
  TableRowsOutlined,
  AddRounded,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardHeader,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { CreateRoleModal, CreateTeamModal, TooltipToggleButton } from "src/components";
import { TeamOverviewSection } from "src/constants";
import { useSettings, useBreakpoints } from "src/hooks";
import { useTeamPageLayoutContext } from "src/pages";
import { AddTeamMemberModal, TeamNetworkGraph, TeamNetworkList, TeamNetworkListFilterButton } from ".";

interface IProps {
  teamSlug: string;
  sx?: SxProps;
}

const TeamNetwork = (props: IProps) => {
  const { teamOverviewSettings, updateTeamOverviewSettings } = useSettings();
  const { isMdUp } = useBreakpoints();
  const { team } = useTeamPageLayoutContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false);
  const [isAddTeamMemberModalOpen, setIsAddTeamMemberModalOpen] = useState(false);
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);

  const canAdd = team.isAdministrator || team.isMember;

  return (
    <Card sx={{ ...props.sx }}>
      <CardHeader
        title={
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Stack direction="row" spacing={0.5} sx={{
              alignItems: "center"
            }}>
              <Typography variant="h6">Network</Typography>
              {isMdUp && (
                <IconButton
                  variant="tertiary"
                  onClick={() => {
                    updateTeamOverviewSettings({
                      expandedSection:
                        teamOverviewSettings.expandedSection === TeamOverviewSection.Network
                          ? undefined
                          : TeamOverviewSection.Network,
                    });
                    window.scrollTo({ top: 0 });
                  }}
                >
                  {teamOverviewSettings.expandedSection === TeamOverviewSection.Network ? (
                    <CloseFullscreenRounded />
                  ) : (
                    <OpenInFullRounded />
                  )}
                </IconButton>
              )}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {canAdd && (
                <>
                  <IconButton variant="tertiary" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <AddRounded />
                  </IconButton>

                  <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                    <MenuItem
                      onClick={() => {
                        setIsAddTeamModalOpen(true);
                        setMenuAnchor(null);
                      }}
                    >
                      Add Team
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setIsAddRoleModalOpen(true);
                        setMenuAnchor(null);
                      }}
                    >
                      Add Role
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setIsAddTeamMemberModalOpen(true);
                        setMenuAnchor(null);
                      }}
                    >
                      Add Team Member
                    </MenuItem>
                  </Menu>
                </>
              )}

              {teamOverviewSettings.networkListView && (
                <TeamNetworkListFilterButton
                  teamSlug={props.teamSlug}
                  value={teamOverviewSettings.networkListViewFilterByOption}
                  onChange={(value) => updateTeamOverviewSettings({ networkListViewFilterByOption: value })}
                />
              )}

              <ToggleButtonGroup
                variant="tertiary"
                value={teamOverviewSettings.networkListView}
                color="primary"
                size="small"
                exclusive
                onChange={(_e, value: boolean) => updateTeamOverviewSettings({ networkListView: value })}
              >
                <TooltipToggleButton title="Graph View" value={false}>
                  <HubRounded fontSize="small" />
                </TooltipToggleButton>

                <TooltipToggleButton title="List View" value={true}>
                  <TableRowsOutlined fontSize="small" />
                </TooltipToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        }
      />

      <Divider />

      {!teamOverviewSettings.networkListView && (
        <Box
          sx={{
            height: { xs: "100vw", md: 670 },
            width: "100%",
          }}
        >
          <TeamNetworkGraph teamSlug={props.teamSlug} />
        </Box>
      )}

      {teamOverviewSettings.networkListView && (
        <TeamNetworkList
          teamSlug={props.teamSlug}
          filter={(teamNetworkObject) => {
            if (teamOverviewSettings.networkListViewFilterByOption === undefined) {
              return true;
            } else {
              return teamNetworkObject.type.valueOf() === teamOverviewSettings.networkListViewFilterByOption;
            }
          }}
        />
      )}

      <CreateTeamModal
        visible={isAddTeamModalOpen}
        onCreated={() => setIsAddTeamModalOpen(false)}
        onCancel={() => setIsAddTeamModalOpen(false)}
      />

      <CreateRoleModal
        visible={isAddRoleModalOpen}
        onCreated={() => setIsAddRoleModalOpen(false)}
        onCancelled={() => setIsAddRoleModalOpen(false)}
      />

      <AddTeamMemberModal
        visible={isAddTeamMemberModalOpen}
        initialTeam={team}
        onSuccess={() => setIsAddTeamMemberModalOpen(false)}
        onCancel={() => setIsAddTeamMemberModalOpen(false)}
      />
    </Card>
  );
};

export { TeamNetwork };
